import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ColDef,
  ColumnState,
  ColumnVisibleEvent,
  FilterChangedEvent,
  FilterModel,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  RowDataUpdatedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { WbsItemSearchConditionV2VO } from '../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { CustomEnumValue } from '../../../lib/commons/appFunction'
import { WorkloadUnit } from '../../../lib/functions/workload'
import {
  SearchConditionAndColumnStateV2,
  useSearchConditionAndColumnStateV2StorageService,
} from '../../../services/storage-services/ui-states/searchConditionAndColumnStateV2StorageService'
import { PermanentUiState } from '../../../services/model/uiState'
import { useExcludeInaccessibleRootUuids } from './hooks/useExcludeInaccessibleRootUuids'
import { DISPLAY_DATE_SHORT_FORMAT_WITH_DAY } from '../../../utils/date'
import { storedUiStateWithToolbarToggleAdaptor } from '../../components/toolbars/Toolbar/ToolbarToggle'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import TaskActualWorkDialog from '../../components/dialogs/TaskActualWorkDialog'
import { ROW_HEIGHT } from '../../containers/BulkSheet'
import { BulkSheetView } from '../../containers/BulkSheetView'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import {
  InputError,
  validateNodes,
} from '../../containers/BulkSheetView/lib/validation'
import { usePageState } from '../../hooks/usePageState'
import { SortedColumnState } from '../../model/bulkSheetColumnSortState'
import { PageArea } from '..'
import WbsItemSearchSearchConditionHeaderV2 from './components/Header/WbsItemSearchSearchConditionHeaderV2'
import WbsItemSearchWbsHeader from './components/Header/WbsItemSearchWbsHeader'
import { useWbsItemSearchGridOptions } from './gridOptions'
import { useDialog } from './hooks/dialog'
import {
  useWbsItemSearchSearchConditionV2,
  WbsItemSearchSearchConditionV2,
} from './hooks/useSearchConditionV2'
import { useWbsItemSearchDataV2 } from './hooks/wbsItemSearchDataV2'
import {
  SearchConditionRestoreType,
  useRestoredSearchConditionV2,
} from './hooks/useRestoredSearchConditionV2'
import { QuickFilterKeys, WbsItemSearchRow } from './wbsItemSearch'
import store from '../../../store'
import { FunctionLayer } from '../../../store/functionLayer'
import Immutable from 'immutable'
import {
  addGlobalMessage,
  addScreenMessage,
  MessageLevel,
} from '../../../store/messages'
import { intl } from '../../../i18n'
import { extractValuesFromResponse } from '../../../lib/commons/api'
import { handleWarning } from '../../../handlers/globalErrorHandler'
import { StatusChangePopper } from '../../components/poppers/StatusChangePopper'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import { UiStateKey } from '../../../lib/commons/uiStates'
import SavedUIStateDialog from '../../components/dialogs/SavedUIStateDialog'
import { SavedUIState } from '../../components/dialogs/SavedUIStateDialog/SavedUIStateList'
import { useWorkloadUnit } from '../../hooks/useWorkloadUnit'
import { useExcel } from './hooks/excel'
import { ExcelExportDialog } from '../../containers/BulkSheet/ExcelExportDialog'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import {
  RestoreType,
  useBulkSheetState,
} from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { ExtensionInfo } from '../../hooks/useEntityExtension'
import {
  FIELD_ATTCHMENT,
  FIELD_PARENT_WBS_ITEM_COL,
  FIELD_PATH_COL,
  FIELD_WBSITEM_ACTUALDATE_END,
  FIELD_WBSITEM_ACTUALDATE_START,
} from './gridOptions/columnDefs'
import {
  useWbsItemSearchConditionV2StorageService,
  WbsItemSearchConditionV2StorageService,
} from '../../../services/storage-services/ui-states/wbsItemSearchConditionV2StorageService'
import { requireSave } from '../../../store/requiredSaveData'
import { WbsItemAdditionalPropertyLayoutEntity } from '../../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import Loading from '../../components/process-state-notifications/Loading'
import {
  addCopyURLAndNameMenuItems,
  addCopyURLMenuItems,
  WbsItem,
} from '../../containers/BulkSheetView/gridOptions/contextMenu'
import { getSelectedNode } from '../../containers/BulkSheetView/lib/gridApi'

// --- Types ---

export type WbsItemSearchV2InitializerProps = {
  applicationFunctionUuid: string
  projectUuid: string
  functionLayer: Immutable.Map<number, FunctionLayer>
  selectColumnOptions: {
    [key: string]: CustomEnumValue[]
  }
  loginUserUuid: string
  extensions: ExtensionInfo
  wbsItemAdditionalProperties: WbsItemAdditionalPropertyLayoutEntity
}

type V2ContextProps = WbsItemSearchV2InitializerProps & {
  restoredSearchCondition: WbsItemSearchSearchConditionV2
  searchConditionRepository: WbsItemSearchConditionV2StorageService
  searchConditionRestoreType: SearchConditionRestoreType
}

// --- Constants ---

const EXCEL_EXPORT_EXCLUDE_COLIDS: string[] = [
  'uuid',
  'deliverableAttachmentSummary',
]

const CONVERT_COLUMN_FIELD_MAP = {
  attachment: FIELD_ATTCHMENT,
  'wbsItem.path': FIELD_PATH_COL,
  'parentWbsItem.displayName': FIELD_PARENT_WBS_ITEM_COL,
}
const CONVERT_COLUMN_FIELD_MAP_KEYS = Object.keys(CONVERT_COLUMN_FIELD_MAP)

type WbsItemSearchPageState = {
  workloadUnit: WorkloadUnit
  dateFormat: string
  rowHeight: number
}

const wbsItemSearchDefaultPageState: WbsItemSearchPageState = {
  workloadUnit: WorkloadUnit.HOUR,
  dateFormat: DISPLAY_DATE_SHORT_FORMAT_WITH_DAY,
  rowHeight: ROW_HEIGHT.SMALL,
}

// --- V2 Initializer ---

export const WbsItemSearchV2Initializer = (
  props: WbsItemSearchV2InitializerProps
) => {
  const searchConditionRepository = useWbsItemSearchConditionV2StorageService()

  const {
    initialized: searchConditionRestored,
    restoredSearchCondition,
    restoreType: searchConditionRestoreType,
  } = useRestoredSearchConditionV2(
    props.applicationFunctionUuid,
    props.projectUuid,
    searchConditionRepository
  )

  if (!searchConditionRestored) {
    return <></>
  }

  return (
    <WbsItemSearchContextV2
      {...props}
      restoredSearchCondition={restoredSearchCondition}
      searchConditionRepository={searchConditionRepository}
      searchConditionRestoreType={searchConditionRestoreType}
    />
  )
}

// --- V2 Context ---

const WbsItemSearchContextV2 = ({
  applicationFunctionUuid,
  projectUuid,
  functionLayer,
  selectColumnOptions,
  loginUserUuid,
  extensions,
  restoredSearchCondition,
  searchConditionRepository,
  searchConditionRestoreType,
  wbsItemAdditionalProperties,
}: V2ContextProps) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )
  const [refreshAfterUpdateState, setRefreshAfterUpdateState] =
    useState<boolean>(false)

  const bulkSheetViewRef = useRef<HTMLDivElement>(null)
  const gridOptionsRef = useRef<GridOptions>()
  const gridApi = useRef<GridApi>()
  const quickFilters = useRef<QuickFilterKeys[] | undefined>(undefined)

  const {
    searchCondition,
    saveSearchCondition,
    updateSearchCondition,
    overrideSearchCondition,
    resetSearchCondition,
  } = useWbsItemSearchSearchConditionV2(
    applicationFunctionUuid,
    projectUuid,
    restoredSearchCondition,
    searchConditionRepository
  )
  const restoreTypes = useMemo(
    () =>
      searchConditionRestoreType === 'URL_QUERY'
        ? (['EXPAND_ROW_IDS', 'COLUMN_STATE'] as RestoreType[])
        : undefined,
    [searchConditionRestoreType]
  )

  const bulkSheetState = useBulkSheetState(
    applicationFunctionUuid,
    projectUuid,
    UiStateKey.WbsItemSearchColumnAndFilterState,
    restoreTypes,
    projectUuid
  )

  const {
    hitDataSize,
    count,
    totalCount,
    data,
    refresh,
    refreshSingleRow,
    save,
    saveSingleRow,
    generateWbsItemDeltaRequest,
  } = useWbsItemSearchDataV2({
    applicationFunctionUuid,
    projectUuid,
    searchCondition,
  })
  const prevData = useRef<WbsItemSearchRow[]>([])
  prevData.current = data
  const reload = useRef<Function>()
  reload.current = refresh
  const submit = useRef<Function>()
  submit.current = save
  const refreshSingleRowRef = useRef<Function>()
  refreshSingleRowRef.current = refreshSingleRow
  const saveSingleRowRef = useRef<Function>()
  saveSingleRowRef.current = saveSingleRow

  useEffect(() => {
    if (hitDataSize === 0 && !!gridOptionsRef.current) {
      setLoading(false)
    }
  }, [hitDataSize, gridOptionsRef.current])

  const hasChanged = useCallback((): boolean => {
    return (prevData.current || data).some(v => v.added || v.edited)
  }, [data])

  const dialog = useDialog()
  const columnSetting = useColumnSetting()
  const onExcelExport = useExcel(gridApi.current)

  const { workloadUnit, dateFormat, rowHeight, updatePageState } =
    usePageState<WbsItemSearchPageState>(
      applicationFunctionUuid,
      wbsItemSearchDefaultPageState,
      storedUiStateWithToolbarToggleAdaptor
    )

  const onClickedStatusCell = useCallback(
    (target: EventTarget | undefined, row: WbsItemSearchRow | undefined) => {
      const getAgCellElement = (elem): any => {
        if (!elem || elem.className.toString().includes('sevend-ag-cell')) {
          return elem
        }
        return getAgCellElement(elem.parentElement)
      }
      const targetElement = getAgCellElement(target)
      if (!target || !row?.wbsItem) return
      dialog.openStatusPopper(
        targetElement,
        row.wbsItem,
        generateWbsItemDeltaRequest(row)!
      )
    },
    [dialog, generateWbsItemDeltaRequest]
  )

  const onClickedActualHourCell = useCallback(
    (row: WbsItemSearchRow | undefined) => {
      const uuid: string | undefined = row?.wbsItem?.uuid
      if (!uuid) return
      dialog.openActualResult(uuid)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dialog.openActualResult]
  )

  const reloadSingleRow = useCallback((wbsItemUuid: string | undefined) => {
    if (!refreshSingleRowRef.current) return
    refreshSingleRowRef.current(wbsItemUuid)

    if (gridApi.current) {
      let existsEditedRow: boolean = false
      gridApi.current.forEachNode((row: IRowNode<WbsItemSearchRow>) => {
        if (row.data?.edited) existsEditedRow = true
      })
      if (existsEditedRow) store.dispatch(requireSave())
    }
  }, [])

  const submitSingleRow = useCallback(async (wbsItemUuid: string) => {
    if (saveSingleRowRef?.current) {
      await saveSingleRowRef.current(wbsItemUuid)
      refreshSingleRowRef?.current?.(wbsItemUuid)
    }
  }, [])

  const gridOptions = useWbsItemSearchGridOptions({
    projectUuid,
    selectColumnOptionMap: selectColumnOptions,
    onClickedStatusCell,
    extensions,
    onClickedActualHourCell,
    reloadSingleRow,
    submitSingleRow,
    wbsItemAdditionalProperties,
  })
  const contextRef = useRef<Record<string, any>>(gridOptions.context)
  const updateContext = useCallback((partial: Record<string, any>) => {
    contextRef.current = { ...contextRef.current, ...partial }
    gridApi.current?.setGridOption('context', contextRef.current)
  }, [])

  const refreshAll = useCallback(async () => {
    if (!gridOptionsRef.current) return
    setLoading(true)
    try {
      const refreshRows = reload.current ?? refresh
      await refreshRows(projectUuid)
    } finally {
      updateContext({ errors: new InputError() })
      setLoading(false)
      gridApi.current?.refreshCells({ force: true })
    }
  }, [projectUuid, refresh, updateContext])

  const validateBeforeSubmit = useCallback(
    (rows: WbsItemSearchRow[]): InputError | undefined => {
      if (!gridOptionsRef.current) return undefined
      const targetRowUuids = rows.filter(row => row.edited).map(row => row.uuid)
      const errors = validateNodes({
        gridApi: gridApi.current,
        uuids: targetRowUuids,
      })
      updateContext({ errors })
      return errors
    },
    [updateContext]
  )

  const onSubmit = useCallback(async () => {
    if (!gridApi?.current) return
    setLoading(true)
    try {
      gridApi.current?.stopEditing()
      const errors = validateBeforeSubmit(prevData.current)
      if (errors?.hasMessage()) {
        store.dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'global.warning.businessError' }),
            text: errors.toMessage(id => {
              return String(
                (prevData.current?.findIndex(d => d.uuid === id) ?? -1) + 1
              )
            }),
          })
        )
        return
      }

      bulkSheetState.saveImmediately()

      const [
        updateWbsItemResponse,
        updateSprintProductResponse,
        updateSprintBacklogResponse,
      ] = submit.current ? await submit.current() : await save()

      if (updateWbsItemResponse.hasWarning) {
        const messages = extractValuesFromResponse(
          updateWbsItemResponse.json,
          'messages'
        )
        handleWarning(messages, uuid => {
          const target = data.find(v => v.wbsItem?.uuid === uuid)
          return target?.wbsItem?.displayName
        })
      }

      if (
        !updateWbsItemResponse.hasError &&
        !updateWbsItemResponse.hasWarning &&
        !updateSprintProductResponse.hasError &&
        !updateSprintBacklogResponse.hasError
      ) {
        store.dispatch(
          addScreenMessage(applicationFunctionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
          })
        )
        await refreshAll()
      }
    } finally {
      setLoading(false)
    }
  }, [
    gridApi,
    validateBeforeSubmit,
    save,
    data,
    applicationFunctionUuid,
    refreshAll,
    bulkSheetState,
  ])

  // Search condition header Actions ---------------

  const onSearch = useCallback(() => {
    refreshAll()
    saveSearchCondition()
  }, [refreshAll, saveSearchCondition])

  const overrideSearchConditionAndSearch = useCallback(
    (newSearchCondition: WbsItemSearchConditionV2VO) => {
      overrideSearchCondition(newSearchCondition)
      setRefreshAfterUpdateState(true)
    },
    [overrideSearchCondition]
  )

  const permanentSearchConditionStorageService =
    useSearchConditionAndColumnStateV2StorageService(
      applicationFunctionUuid,
      projectUuid
    )
  const { exclude: excludeInaccessibleRootUuids, notify: notifyExcluded } =
    useExcludeInaccessibleRootUuids(applicationFunctionUuid, projectUuid)

  const applyStoredState = useCallback(
    async (state: PermanentUiState<SearchConditionAndColumnStateV2>) => {
      const excludeResult = await excludeInaccessibleRootUuids(
        state.value.searchCondition
      )
      if (excludeResult.hasDeleted) {
        await permanentSearchConditionStorageService.save({
          uuid: state.uuid,
          name: state.name,
          scope: state.scope,
          value: {
            searchCondition: excludeResult.persisted,
            columnState: state.value.columnState,
          },
        })
      }
      notifyExcluded(excludeResult)
      overrideSearchConditionAndSearch(excludeResult.cleaned)
      gridApi.current?.applyColumnState({
        state: state.value.columnState,
      })
    },
    [
      overrideSearchConditionAndSearch,
      excludeInaccessibleRootUuids,
      notifyExcluded,
      permanentSearchConditionStorageService,
    ]
  )

  // Header Actions ---------------

  const onChangeWorkloadUnit = useCallback(
    (workloadUnit: WorkloadUnit) => {
      updatePageState({ workloadUnit })
    },
    [updatePageState]
  )

  const onReload = useCallback(() => {
    if (hasChanged()) {
      dialog.openCancelConfirmation()
    } else {
      refreshAll()
    }
  }, [dialog, hasChanged, refreshAll])

  const onChangeQuickFilters = useCallback((values: string[]) => {
    const filters: QuickFilterKeys[] = values.map(v => QuickFilterKeys[v])
    quickFilters.current = filters
    gridApi.current?.onFilterChanged()
    gridApi.current?.refreshCells({ force: true })
  }, [])

  const onDeleteFilteredColumn = useCallback((column: ColDef) => {
    if (!column || !gridApi.current) return
    const filterModel = gridApi.current?.getFilterModel()
    delete filterModel[column.colId || column.field || '']
    gridApi.current?.setFilterModel(filterModel)
  }, [])

  const resetFilters = useCallback(() => {
    gridApi.current?.setFilterModel([])
  }, [])

  const onDeleteSortedColumn = useCallback(
    (colId: string | ColDef<WbsItemSearchRow>) => {
      gridApi.current?.applyColumnState({
        state: [{ colId: colId.toString(), sort: null }],
      })
    },
    []
  )

  const onDeleteSortedAllColumns = useCallback(() => {
    gridApi.current?.applyColumnState({
      defaultState: { sort: null },
    })
  }, [])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<WbsItemSearchRow>, sort: 'asc' | 'desc' | null) => {
      gridApi.current?.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    []
  )

  const onChangeRowHeight = useCallback(
    (rowHeight: number) => {
      updatePageState({ rowHeight })
    },
    [updatePageState]
  )

  const onChangeDateFormat = useCallback(
    (dateFormat: string) => {
      updatePageState({ dateFormat })
    },
    [updatePageState]
  )

  // BulksSheetView Actions ---------------

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterModel = e.api?.getFilterModel()
      delete filterModel['uuid']
      bulkSheetState.saveFilterState(filterModel)
      setFilteredColumns(
        Object.keys(filterModel)
          .map(col => e.api.getColumnDef(col))
          .filter(v => !!v && v.field !== 'uuid') as ColDef[]
      )
    },
    [bulkSheetState]
  )

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<WbsItemSearchRow>) => {
      if (!_.isEmpty(bulkSheetState.filterState)) {
        gridApi.current?.setFilterModel(
          bulkSheetState.filterState as FilterModel
        )
      }
      if (!_.isEmpty(bulkSheetState.columnState)) {
        event.api.applyColumnState({
          state: bulkSheetState.columnState,
          applyOrder: true,
        })
      }
      bulkSheetState.finishRestoring()
      setLoading(false)
    },
    [bulkSheetState]
  )

  const rememberColumnState = useCallback(() => {
    const columnState = gridApi.current?.getColumnState()
    columnState && bulkSheetState.saveColumnState(columnState)
  }, [bulkSheetState])

  const onSortedColumnsStateChanged = useCallback((e: SortChangedEvent) => {
    const sortedColumns = e.api
      .getColumnState()
      .filter(col => !!col.sort)
      .map(col => {
        return {
          ...e.api.getColumnDef(col.colId),
          colId: col.colId,
        }
      })
      .filter(v => !!v) as ColDef[]
    e.api.setGridOption('suppressRowDrag', 0 < sortedColumns.length)

    const columnState = gridApi.current?.getColumnState()
    const sortedColumnState: { [colId: string]: ColumnState } = {}
    columnState &&
      columnState.forEach(state => {
        if (state.sort) {
          sortedColumnState[state.colId] = state
        }
      })
    const sortedColumnsState: SortedColumnState[] = sortedColumns.map(col => {
      return {
        colId: col.colId,
        field: col.field,
        headerName: col.headerName,
        sort: col.colId ? sortedColumnState[col.colId]?.sort : null,
      }
    })

    setSortColumnsState(sortedColumnsState)
  }, [])

  const onColumnVisible = useCallback(
    (e: ColumnVisibleEvent) => {
      rememberColumnState()
      onSortedColumnsStateChanged(e as unknown as SortChangedEvent)
    },
    [rememberColumnState, onSortedColumnsStateChanged]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      rememberColumnState()
      onSortedColumnsStateChanged(e)
    },
    [rememberColumnState, onSortedColumnsStateChanged]
  )

  const onRowDataUpdated = useCallback(
    (e: RowDataUpdatedEvent<WbsItemSearchRow>) =>
      e.api?.refreshCells({
        columns: [
          'rowNumber',
          FIELD_WBSITEM_ACTUALDATE_START,
          FIELD_WBSITEM_ACTUALDATE_END,
        ],
        force: true,
      }),
    []
  )

  const onGridReady = useCallback(
    (event: GridReadyEvent<WbsItemSearchRow>) => {
      gridOptionsRef.current = gridOptions
      gridApi.current = event.api
    },
    [gridOptions]
  )

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<WbsItemSearchRow>): boolean => {
      if (
        quickFilters.current?.includes(QuickFilterKeys.ACCOUNTABLE) &&
        node.data?.wbsItem.accountable?.uuid !== loginUserUuid
      ) {
        return false
      }
      if (
        quickFilters.current?.includes(
          QuickFilterKeys.RESPONSIBLE_OR_ASSIGNEE
        ) &&
        node.data?.wbsItem.responsible?.uuid !== loginUserUuid &&
        node.data?.wbsItem.assignee?.uuid !== loginUserUuid
      ) {
        return false
      }
      return true
    },
    [loginUserUuid, quickFilters]
  )

  const isExternalFilterPresent = useCallback(() => true, [])

  const initializeColumnState = useCallback(() => {
    gridApi.current?.resetColumnState()
    const filterModel = gridApi.current?.getFilterModel()
    const quick = filterModel?.['uuid']
    gridApi.current?.setFilterModel(quick ? { uuid: quick } : null)
  }, [])

  const workloadUnitState = useWorkloadUnit(workloadUnit)
  useEffect(() => {
    updateContext({ workloadUnit, dateFormat, workloadUnitState })
    gridApi.current?.refreshCells({ force: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workloadUnit, dateFormat, workloadUnitState])

  useEffect(() => {
    gridApi.current?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowHeight])

  const convertColumnStateToNewColID = useCallback((colState: ColumnState) => {
    if (CONVERT_COLUMN_FIELD_MAP_KEYS.includes(colState.colId)) {
      return {
        ...colState,
        colId: CONVERT_COLUMN_FIELD_MAP[colState.colId],
      }
    }
    return colState
  }, [])

  const convertFilterStateToNewColId = useCallback(
    (filterState: { [key: string]: string }) => {
      const newFilterState = _.cloneDeep(filterState)
      CONVERT_COLUMN_FIELD_MAP_KEYS.forEach((key: string) => {
        if (key in filterState) {
          newFilterState[CONVERT_COLUMN_FIELD_MAP[key]] = filterState[key]
        }
      })
      return newFilterState
    },
    []
  )

  // Key bind ------
  useKeyBind(
    [
      {
        key: KEY_SAVE,
        fn: onSubmit,
        stopDefaultBehavior: true,
      },
    ],
    []
  )

  const onCloseSearchItemLabel = useCallback(() => {
    setRefreshAfterUpdateState(true)
  }, [])

  useEffect(() => {
    if (!refreshAfterUpdateState) return
    onSearch()
    setRefreshAfterUpdateState(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshAfterUpdateState])

  const contextMenu = useCallback((params: GetContextMenuItemsParams) => {
    if (!params.node) return []
    const selectedWbsItems: WbsItem[] | undefined = getSelectedNode(
      params.api
    ).map(node => node.data?.wbsItem)
    return [
      addCopyURLMenuItems(selectedWbsItems),
      addCopyURLAndNameMenuItems(selectedWbsItems),
    ]
  }, [])

  // Restore state from bulkSheetState ---------------
  useEffect(() => {
    if (
      !gridApi.current ||
      bulkSheetState.restored ||
      !bulkSheetState.initialized
    ) {
      return
    }
    if (!_.isEmpty(bulkSheetState.filterState)) {
      gridApi.current?.setFilterModel(bulkSheetState.filterState as FilterModel)
    }
    if (!_.isEmpty(bulkSheetState.columnState)) {
      gridApi.current?.applyColumnState({
        state: bulkSheetState.columnState,
        applyOrder: true,
      })
    }
    bulkSheetState.finishRestoring()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi.current, bulkSheetState])

  return (
    <PageArea>
      {functionLayer.size === 1 && (
        <SavePopper loading={loading} onSubmit={onSubmit} />
      )}
      <Loading isLoading={loading} elem={bulkSheetViewRef.current} />
      <WbsItemSearchSearchConditionHeaderV2
        searchCondition={searchCondition}
        search={onSearch}
        count={count}
        total={totalCount}
        onChange={updateSearchCondition}
        resetCondition={resetSearchCondition}
        functionUuid={applicationFunctionUuid}
        applyStoredState={applyStoredState}
        onCloseSearchItemLabel={onCloseSearchItemLabel}
        isLoading={loading}
        permanentSearchConditionStorageService={
          permanentSearchConditionStorageService
        }
      />
      <WbsItemSearchWbsHeader
        workloadUnit={workloadUnit}
        onChangeWorkloadUnit={onChangeWorkloadUnit}
        onCancel={onReload}
        isLoading={loading}
        onChangeQuickFilters={onChangeQuickFilters}
        quickFilters={quickFilters.current ?? []}
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeleteFilteredColumn}
        resetFilters={resetFilters}
        sortColumnsState={sortColumnsState}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        rowHeight={rowHeight}
        onChangeHeight={onChangeRowHeight}
        onClickExportExcel={dialog.openExcelExport}
        onClickFavoriteColumnFilterButton={dialog.openUiState}
        onClickColumnSettingButton={columnSetting.toggle}
        columnSettingOpen={columnSetting.isOpen}
        currentFormat={dateFormat}
        onChangeDateFormat={onChangeDateFormat}
      />
      <BulkSheetView
        getContextMenuItems={contextMenu}
        doesExternalFilterPass={doesExternalFilterPass}
        gridOptions={gridOptions}
        ref={bulkSheetViewRef}
        rowData={data}
        rowHeight={rowHeight}
        isExternalFilterPresent={isExternalFilterPresent}
        onCellDoubleClicked={dialog.closeStatusPopper}
        onColumnMoved={rememberColumnState}
        onColumnResized={rememberColumnState}
        onColumnVisible={onColumnVisible}
        onFilterChanged={onFilterChanged}
        onFirstDataRendered={onFirstDataRendered}
        onGridReady={onGridReady}
        onRowDataUpdated={onRowDataUpdated}
        onSortChanged={onSortChanged}
      />
      {dialog.cancelConfirmation && (
        <CancelConfirmDialog
          open={true}
          onConfirm={() => {
            dialog.closeCancelConfirmation()
            refreshAll()
          }}
          onClose={dialog.closeCancelConfirmation}
        />
      )}
      {dialog.actualResult && (
        <TaskActualWorkDialog
          open={true}
          taskUuid={dialog.taskUuid ?? ''}
          closeHandler={dialog.closeActualResult}
        />
      )}
      <StatusChangePopper
        anchorEl={dialog.statusPopperParams.anchorEl}
        projectUuid={projectUuid}
        wbsItem={dialog.statusPopperParams.wbsItem}
        wbsItemDelta={dialog.statusPopperParams.wbsItemDelta}
        onClose={dialog.closeStatusPopper}
        onAfterUpdate={() =>
          reloadSingleRow(dialog.statusPopperParams.wbsItem?.uuid)
        }
      />
      <ColumnSettingPopper
        anchorEl={columnSetting.anchorEl}
        open={columnSetting.isOpen}
        gridApi={gridApi.current ?? undefined}
        close={columnSetting.close}
        height={bulkSheetViewRef.current?.offsetHeight}
        openSavedUiStateDialog={dialog.openUiState}
        initializeColumnState={initializeColumnState}
        applicationFunctionUuid={applicationFunctionUuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetState.columnState}
        offset={110}
      />
      {dialog.uiState && (
        <SavedUIStateDialog
          applicationFunctionUuid={applicationFunctionUuid}
          open={true}
          title={intl.formatMessage({
            id: 'savedUIState.BULK_SHEET_UI_STATE_COLUMN_AND_FILTER',
          })}
          uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
          sharable={true}
          currentUIState={{
            columnState: bulkSheetState.columnState,
            filterState: bulkSheetState.filterState,
          }}
          onSelect={(uiState: SavedUIState) => {
            const { columnState, column, filterState, filter } = uiState.UIState
            const applyColumnState: ColumnState[] =
              columnState ?? column.map(convertColumnStateToNewColID)
            if (applyColumnState) {
              bulkSheetState.saveColumnState(applyColumnState)
              gridApi.current?.applyColumnState({
                state: applyColumnState,
                applyOrder: true,
              })
            }
            const applyFilterState =
              filterState ?? convertFilterStateToNewColId(filter)
            if (applyFilterState) {
              bulkSheetState.saveFilterState(applyFilterState)
              gridApi.current?.setFilterModel(applyFilterState)
            }
            dialog.closeUiState()
          }}
          onClose={dialog.closeUiState}
        />
      )}
      {dialog.excelExport && (
        <ExcelExportDialog
          onClose={dialog.closeExcelExport}
          onSubmit={onExcelExport}
          gridApi={gridApi.current}
          excludeColIds={EXCEL_EXPORT_EXCLUDE_COLIDS}
        />
      )}
    </PageArea>
  )
}
